// Left rail for /decks/recipes — mirrors CatalogRail layout (pinned scope + list)
// but single-select categories only. Used when viewport is wider than SM.

import React, { useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { RAIL_WIDTH } from '@/components/Catalog/CatalogRail';

const CATEGORY_ACCENT = '#6366F1';
const FAVORITES_ACCENT = '#ef4444';

/** Sentinel `selectedSlug` value for the Favorites scope (not a real category slug). */
export const DECK_RECIPES_RAIL_FAVORITES_SLUG = '__favorites__';

const ALL_ITEM = {
  id: 'all',
  slug: null,
  label: 'All recipes',
  icon: 'grid-2',
  color: '#3B82F6',
};

function RailRow({ icon, label, count, isActive, color, onPress, showZeroCount }) {
  const { colors } = useTheme();
  const [hovered, setHovered] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[
        styles.row,
        {
          backgroundColor: isActive
            ? `${color}1F`
            : (hovered ? colors.solid_color_tertiary : 'transparent'),
        },
        Platform.OS === 'web' && { cursor: 'pointer' },
      ]}
    >
      <Icon name={icon} size={11} color={isActive ? color : colors.text_color_secondary} />
      <Text
        size="s"
        weight={isActive ? 'bold' : 'semibold'}
        numberOfLines={2}
        style={{ flex: 1, color: isActive ? color : colors.text_color_primary }}
      >
        {label}
      </Text>
      {count != null && (showZeroCount || count > 0) && (
        <Text
          size="s"
          weight="semibold"
          style={{
            color: isActive ? color : colors.text_color_tertiary,
            fontVariant: ['tabular-nums'],
          }}
        >
          {count > 999 ? '999+' : count}
        </Text>
      )}
    </Pressable>
  );
}

/**
 * @param {Array<{ slug: string, name: string }>} props.categories
 * @param {Map<string, number>} props.countBySlug
 * @param {string|null} props.selectedSlug — null = all; {@link DECK_RECIPES_RAIL_FAVORITES_SLUG} = saved only
 * @param {number} props.totalCount
 * @param {number} props.favoritesCount — recipes in the current catalog that are also user favorites
 * @param {() => void} props.onSelectAll
 * @param {() => void} props.onSelectFavorites
 * @param {(slug: string) => void} props.onSelectSlug
 */
export default function DeckRecipesCatalogRail({
  categories,
  countBySlug,
  selectedSlug,
  totalCount,
  favoritesCount,
  onSelectAll,
  onSelectFavorites,
  onSelectSlug,
}) {
  const { colors } = useTheme();
  return (
    <View style={[styles.rail, { borderRightColor: colors.border_color_secondary, backgroundColor: colors.solid_color_primary }]}>
      <View style={{ paddingTop: 8, paddingBottom: 16 }}>
        <RailRow
          icon={ALL_ITEM.icon}
          label={ALL_ITEM.label}
          count={totalCount}
          isActive={!selectedSlug}
          color={ALL_ITEM.color}
          onPress={onSelectAll}
        />

        <RailRow
          icon="heart"
          label="Favorites"
          count={favoritesCount}
          isActive={selectedSlug === DECK_RECIPES_RAIL_FAVORITES_SLUG}
          color={FAVORITES_ACCENT}
          onPress={onSelectFavorites}
          showZeroCount
        />

        <View style={[styles.divider, { backgroundColor: colors.border_color_secondary }]} />

        <View style={styles.sectionHeader}>
          <Text size="s" weight="bold" color="secondary" style={styles.sectionHeaderLabel}>
            Categories
          </Text>
        </View>

        {categories.map((cat) => {
          const slug = String(cat.slug).toLowerCase();
          const n = countBySlug?.get?.(slug) ?? 0;
          const active = selectedSlug === slug;
          return (
            <RailRow
              key={slug}
              icon="layer-group"
              label={cat.name || slug}
              count={n}
              isActive={active}
              color={CATEGORY_ACCENT}
              onPress={() => onSelectSlug(slug)}
            />
          );
        })}

        {categories.length === 0 && (
          <View style={{ padding: 12 }}>
            <Text size="s" color="tertiary">No categories yet.</Text>
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  rail: {
    width: RAIL_WIDTH,
    borderRightWidth: 1,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    minHeight: 32,
    paddingHorizontal: 10,
    marginHorizontal: 4,
    borderRadius: 6,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    marginVertical: 10,
    marginHorizontal: 12,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingBottom: 6,
  },
  sectionHeaderLabel: {
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
});
